/**
 * @feature quote-requests
 * @layer utils
 *
 * "동일 지역만 허용" 옵션이 켜진 견적요청의 참여 가능 지역 산출 유틸.
 *
 * 규칙: 발주자 주소의 시/도 + **인접한 2개 시/도** (총 3개 지역) 공급사만 참여 가능.
 * 이 파일은 그 "인접한 2개" 매핑을 하드코딩한 SSOT 다.
 *
 * ⚠️ 인접 매핑은 지리 + 물류 편의를 감안한 임의 조합이다. 각 시/도는 실제로 3~6개 시/도와
 * 접하지만 규칙상 2개로 고정해야 하므로, 인구·산업 밀집도가 높은 두 곳을 우선 선택했다.
 * 정책 변경 시 이 맵만 갱신하면 된다.
 */

/** 정규화된 시/도 키 (17개 광역자치단체). 파일 내부에서만 사용. */
type NormalizedProvince =
  | "서울"
  | "부산"
  | "대구"
  | "인천"
  | "광주"
  | "대전"
  | "울산"
  | "세종"
  | "경기"
  | "강원"
  | "충북"
  | "충남"
  | "전북"
  | "전남"
  | "경북"
  | "경남"
  | "제주";

/**
 * "서울특별시" · "경기도" · "강원특별자치도" 같은 접미사를 걷어 정규화 키로 변환.
 * 매칭 안 되면 null. 파일 내부에서만 사용.
 */
function normalizeProvince(
  input: string | null | undefined,
): NormalizedProvince | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;

  // 접미사 제거: 특별시 / 광역시 / 특별자치시 / 특별자치도 / 도
  const stripped = raw
    .replace(/(특별자치도|특별자치시|특별시|광역시|도)$/u, "")
    .trim();

  // 축약형 정규화 (전북, 전남, 경북, 경남, 충북, 충남, 강원 는 이미 축약형)
  const aliasMap: Record<string, NormalizedProvince> = {
    서울: "서울",
    부산: "부산",
    대구: "대구",
    인천: "인천",
    광주: "광주",
    대전: "대전",
    울산: "울산",
    세종: "세종",
    경기: "경기",
    강원: "강원",
    충북: "충북",
    충청북: "충북",
    충남: "충남",
    충청남: "충남",
    전북: "전북",
    전라북: "전북",
    전남: "전남",
    전라남: "전남",
    경북: "경북",
    경상북: "경북",
    경남: "경남",
    경상남: "경남",
    제주: "제주",
  };

  return aliasMap[stripped] ?? null;
}

/**
 * 인접 매핑 — 각 시/도가 우선 접하는 **2개** 시/도.
 *
 * 참고 (실제 인접):
 * - 경기 는 서울/인천/충남/충북/강원 5개와 접함 → 최대 인구·산업 밀집인 서울/인천 선택
 * - 강원 은 경기/충북/경북 3개와 접함 → 경기/경북 선택
 * - 충북 은 세종/대전/충남/경기/강원/경북 6개와 접함 → 세종/대전 선택 (수도권 물류 편의)
 * - 제주 는 육상 인접 없음 → 자기 자신만 노출 (아래 getEligibleRegions 특수 처리)
 */
const ADJACENCY: Record<NormalizedProvince, NormalizedProvince[]> = {
  서울: ["인천", "경기"],
  인천: ["서울", "경기"],
  경기: ["서울", "인천"],
  강원: ["경기", "경북"],
  충북: ["세종", "대전"],
  충남: ["세종", "대전"],
  세종: ["대전", "충남"],
  대전: ["세종", "충남"],
  전북: ["전남", "충남"],
  전남: ["전북", "광주"],
  광주: ["전남", "전북"],
  경북: ["대구", "강원"],
  대구: ["경북", "경남"],
  경남: ["부산", "대구"],
  부산: ["경남", "울산"],
  울산: ["부산", "경북"],
  제주: [], // 섬 — 인접 없음
};

/** 정규화 키를 사용자에게 노출할 정식 명칭으로 되돌림. */
const DISPLAY_NAME: Record<NormalizedProvince, string> = {
  서울: "서울특별시",
  부산: "부산광역시",
  대구: "대구광역시",
  인천: "인천광역시",
  광주: "광주광역시",
  대전: "대전광역시",
  울산: "울산광역시",
  세종: "세종특별자치시",
  경기: "경기도",
  강원: "강원특별자치도",
  충북: "충청북도",
  충남: "충청남도",
  전북: "전북특별자치도",
  전남: "전라남도",
  경북: "경상북도",
  경남: "경상남도",
  제주: "제주특별자치도",
};

export interface EligibleRegionsResult {
  /** 자기 시/도 + 인접 2개 (제주는 자기 시/도만). 표기용 정식 명칭. */
  displayNames: string[];
  /** 인접 매핑에 없는 값이 들어온 경우 true (fallback: 자기만 노출). */
  unknown: boolean;
}

/**
 * 발주자 시/도 문자열을 받아 참여 가능 시/도 리스트를 반환.
 *
 * - 정규 시/도 → 자기 + 인접 2개 (제주는 자기만)
 * - 미매칭   → 입력을 그대로 하나만 노출 + `unknown: true`
 */
export function getEligibleRegions(
  buyerProvince: string | null | undefined,
): EligibleRegionsResult {
  const key = normalizeProvince(buyerProvince);
  if (!key) {
    const fallback = buyerProvince?.trim() ?? "";
    return {
      displayNames: fallback ? [fallback] : [],
      unknown: true,
    };
  }
  const keys: NormalizedProvince[] = [key, ...ADJACENCY[key]];
  return {
    displayNames: keys.map((k) => DISPLAY_NAME[k]),
    unknown: false,
  };
}
